import { useLocalization } from '../context/LocalizationContext';
import { Icon } from './Icon';

export function JurisdictionSwitcherModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { country, setCountry, allJurisdictions, jurisdiction } = useLocalization();

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'grid',
        placeItems: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        className="panel"
        style={{
          width: '100%',
          maxWidth: 680,
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 20px 45px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          animation: 'fadeIn 180ms ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 24px',
            background: 'var(--brand-gradient)',
            color: '#ffffff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.9, fontWeight: 700 }}>
              <span style={{ fontSize: 14 }}>🌐</span>
              <span>International Accounting & Compliance Engine</span>
            </div>

            <h2 style={{ margin: '4px 0 0 0', fontSize: 18, fontWeight: 700, color: '#ffffff' }}>
              Select Operating Jurisdiction
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '50%',
              width: 32,
              height: 32,
              color: '#ffffff',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
            }}
          >
            <Icon name="xCircle" size="sm" />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 24, maxHeight: '72vh', overflowY: 'auto' }}>
          <p style={{ margin: '0 0 16px 0', fontSize: 13, color: 'var(--ink-secondary)', lineHeight: 1.5 }}>
            Switching jurisdiction adapts currency symbols, payroll withholdings, tax identifiers (EIN / GSTIN / VAT), and accounting charts across the platform in real-time.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            {allJurisdictions.map((j) => {
              const isSelected = j.countryCode === country;
              return (
                <div
                  key={j.countryCode}
                  onClick={() => {
                    setCountry(j.countryCode);
                    onClose();
                  }}
                  style={{
                    border: isSelected ? '2px solid var(--brand-primary)' : '1px solid var(--border-subtle)',
                    background: isSelected ? 'var(--persona-bg)' : 'var(--bg-field)',
                    borderRadius: 'var(--radius-md)',
                    padding: '14px 16px',
                    cursor: 'pointer',
                    transition: 'all 150ms ease',
                    position: 'relative',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 24, lineHeight: 1 }}>{j.flag}</span>
                      <div>
                        <strong style={{ fontSize: 14, color: isSelected ? 'var(--brand-primary)' : 'var(--ink)' }}>
                          {j.countryName}
                        </strong>
                        <div className="mono muted" style={{ fontSize: 11 }}>
                          {j.currency} ({j.currencySymbol}) · {j.accountingStandard}
                        </div>
                      </div>
                    </div>
                    {isSelected && (
                      <span className="badge badge-active" style={{ fontSize: 9 }}>Active</span>
                    )}
                  </div>

                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink-secondary)', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div>
                      <span style={{ fontWeight: 600 }}>Tax ID:</span> {j.taxIdLabel}
                    </div>
                    <div>
                      <span style={{ fontWeight: 600 }}>Invoice Tax:</span> {j.invoiceTaxLabel} ({j.defaultTaxRatePercent}%)
                    </div>
                    <div>
                      <span style={{ fontWeight: 600 }}>Statutory:</span> {j.statutoryWithholdingLines.slice(0, 2).join(', ')}...
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Current Active Summary Footnote */}
          <div
            style={{
              marginTop: 18,
              padding: 12,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span style={{ fontSize: 20 }}>{jurisdiction.flag}</span>
            <div style={{ flex: 1 }}>
              <strong>Current Entity: {jurisdiction.sampleOrgName}</strong>
              <div className="muted" style={{ fontSize: 11 }}>
                Operating under {jurisdiction.accountingStandard} with {jurisdiction.taxIdLabel}: <span className="mono">{jurisdiction.taxIdPlaceholder}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 24px',
            background: 'var(--bg-field)',
            borderTop: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <button onClick={onClose} className="btn-quiet btn-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
